import React, { useState, useEffect } from 'react';
import { getFirestore, collection, addDoc, updateDoc, deleteDoc, doc, getDocs, query, where, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import './OperatorEkle.css';
import { formatTrDateOnly, toIsoUtcString } from '../utils/hopsDate';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { auth } from './firebaseConfig';

const formatPhoneDisplay = (value) => {
  // Eğer sadece +90 ise, +90 döndür
  if (value === '+90' || value === '+90 ') {
    return '+90 ';
  }
  
  // Eğer +90 ile başlamıyorsa, +90 ile başlat
  if (!value.startsWith('+90')) {
    // Sadece rakamları al
    const numbers = value.replace(/\D/g, '');
    
    // Eğer 90 ile başlıyorsa, 90'ı çıkar
    let cleanNumbers = numbers;
    if (numbers.startsWith('90') && numbers.length > 2) {
      cleanNumbers = numbers.slice(2);
    }
    
    // Maksimum 10 hane
    cleanNumbers = cleanNumbers.slice(0, 10);
    
    // +90 ile başlat ve formatla
    let formatted = '+90 ';
    if (cleanNumbers.length > 0) {
      formatted += cleanNumbers.slice(0, 3);
      if (cleanNumbers.length > 3) {
        formatted += ' ' + cleanNumbers.slice(3, 6);
        if (cleanNumbers.length > 6) {
          formatted += ' ' + cleanNumbers.slice(6, 8);
          if (cleanNumbers.length > 8) {
            formatted += ' ' + cleanNumbers.slice(8, 10);
          }
        }
      }
    }
    
    return formatted;
  }
  
  // +90 ile başlıyorsa, +90'dan sonrasını al ve formatla
  const afterPrefix = value.slice(3).trim(); // '+90' sonrası, boşlukları temizle
  
  // Eğer +90'dan sonra hiçbir şey yoksa, +90 döndür
  if (!afterPrefix) {
    return '+90 ';
  }
  
  const numbers = afterPrefix.replace(/\D/g, '');
  
  // Maksimum 10 hane
  const cleanNumbers = numbers.slice(0, 10);
  
  // +90 ile başlat ve formatla
  let formatted = '+90 ';
  if (cleanNumbers.length > 0) {
    formatted += cleanNumbers.slice(0, 3);
    if (cleanNumbers.length > 3) {
      formatted += ' ' + cleanNumbers.slice(3, 6);
      if (cleanNumbers.length > 6) {
        formatted += ' ' + cleanNumbers.slice(6, 8);
        if (cleanNumbers.length > 8) {
          formatted += ' ' + cleanNumbers.slice(8, 10);
        }
      }
    }
  }
  
  return formatted;
};

const cleanPhoneNumber = (phone) => {
  // Sadece rakamları al
  const numbers = phone.replace(/\D/g, '');
  
  // Eğer 90 ile başlıyorsa, 90'ı çıkar
  let cleanNumbers = numbers;
  if (numbers.startsWith('90') && numbers.length > 2) {
    cleanNumbers = numbers.slice(2);
  }
  
  // Son 10 haneyi al ve +90 ekle
  const lastTenDigits = cleanNumbers.slice(-10);
  return '+90' + lastTenDigits;
};

const EditOperatorModal = ({ open, onClose, operator, onSave }) => {
  const [name, setName] = useState(operator?.name || '');
  const [surname, setSurname] = useState(operator?.surname || '');
  const [phoneNumber, setPhoneNumber] = useState(operator?.phoneNumber || '+90 ');
  const [mesaiBaslangic, setMesaiBaslangic] = useState(operator?.mesaiBaslangic || '09:00');
  const [mesaiBitis, setMesaiBitis] = useState(operator?.mesaiBitis || '18:00');

  useEffect(() => {
    if (operator) {
      setName(operator.name);
      setSurname(operator.surname);
      setPhoneNumber(operator.phoneNumberDisplay || operator.phoneNumber || '+90');
      setMesaiBaslangic(operator.mesaiBaslangic || '09:00');
      setMesaiBitis(operator.mesaiBitis || '18:00');
    }
  }, [operator]);

  const handleSubmit = () => {
    onSave({ 
      name, 
      surname, 
      phoneNumber: cleanPhoneNumber(phoneNumber),
      phoneNumberDisplay: phoneNumber,
      mesaiBaslangic,
      mesaiBitis
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Operatör Düzenle</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Ad"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextField
          margin="dense"
          label="Soyad"
          type="text"
          fullWidth
          value={surname}
          onChange={(e) => setSurname(e.target.value)}
          required
        />
        <TextField
          margin="dense"
          label="Telefon"
          type="tel"
          fullWidth
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(formatPhoneDisplay(e.target.value))}
          required
          inputProps={{
            pattern: "\\+90 [0-9]{3} [0-9]{3} [0-9]{2} [0-9]{2}",
          }}
          helperText="Format: +90 5XX XXX XX XX"
        />
        <div className="mesai-container">
          <TextField
            margin="dense"
            label="Mesai Başı"
            type="time"
            value={mesaiBaslangic}
            onChange={(e) => setMesaiBaslangic(e.target.value)}
            onClick={(e) => {
              // Input'a focus ver ve time picker'ı aç
              const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
              if (input?.showPicker) {
                input.showPicker();
              } else if (input) {
                input.focus();
              }
            }}
            required
            InputLabelProps={{ shrink: true }}
            sx={{
              flex: 1,
              marginRight: 1,
              '& .MuiInputBase-input': {
                cursor: 'pointer'
              },
              '& .MuiInputBase-root': {
                cursor: 'pointer'
              }
            }}
          />
          <TextField
            margin="dense"
            label="Mesai Sonu"
            type="time"
            value={mesaiBitis}
            onChange={(e) => setMesaiBitis(e.target.value)}
            onClick={(e) => {
              // Input'a focus ver ve time picker'ı aç
              const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
              if (input?.showPicker) {
                input.showPicker();
              } else if (input) {
                input.focus();
              }
            }}
            required
            InputLabelProps={{ shrink: true }}
            sx={{
              flex: 1,
              marginLeft: 1,
              '& .MuiInputBase-input': {
                cursor: 'pointer'
              },
              '& .MuiInputBase-root': {
                cursor: 'pointer'
              }
            }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          İptal
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
          Kaydet
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const OperatorEkle = () => {
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('+90 ');
  const [mesaiBaslangic, setMesaiBaslangic] = useState('09:00');
  const [mesaiBitis, setMesaiBitis] = useState('18:00');
  const [operators, setOperators] = useState([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const db = getFirestore();

  // Kullanıcının email'ini al
  useEffect(() => {
    const getUserEmail = async () => {
      if (auth.currentUser?.email) {
        setUserEmail(auth.currentUser.email);
      }
    };
    getUserEmail();
  }, []);

  // Sadece kendi email'inin altındaki operatörlerini getir
  useEffect(() => {
    const fetchOperators = async () => {
      if (!userEmail) return;
      
      try {
        // User email bazlı operatör koleksiyonu
        const operatorsCollection = collection(db, `users/${userEmail}/operators`);
        const operatorSnapshot = await getDocs(operatorsCollection);
        const operatorList = operatorSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setOperators(operatorList);
      } catch (error) {
        console.error('Operatörler getirilirken hata:', error);
      }
    };

    fetchOperators();
  }, [db, userEmail]);

  // Sıralama fonksiyonu
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Sıralanmış operatör listesi
  const sortedOperators = [...operators].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
    }
    if (typeof bValue === 'string') {
      bValue = bValue.toLowerCase();
    }

    if (sortOrder === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Sıralama ikonu
  const renderSortIcon = (column) => {
    if (sortBy !== column) return null;
    return sortOrder === 'asc' ? ' 🔼' : ' 🔽';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!userEmail) {
      alert('Kullanıcı bilgisi alınamadı!');
      return;
    }

    const cleanedPhoneNumber = cleanPhoneNumber(phoneNumber);

    try {
      // Önce bu telefon numarasının başka bir firmada kullanılıp kullanılmadığını kontrol et
      const existingOperatorQuery = query(
        collection(db, 'operators'),
        where('phoneNumber', '==', cleanedPhoneNumber)
      );
      const existingOperatorSnapshot = await getDocs(existingOperatorQuery);
      
      if (!existingOperatorSnapshot.empty) {
        const existingOperator = existingOperatorSnapshot.docs[0].data();
        if (existingOperator.userEmail !== userEmail) {
          alert(`Bu telefon numarası (${phoneNumber}) zaten başka bir firma tarafından kullanılıyor!\n\nOperatör: ${existingOperator.name}\nFirma: ${existingOperator.userEmail}\n\nLütfen farklı bir telefon numarası kullanın.`);
          return;
        } else {
          // Aynı firmada aynı numara varsa
          alert(`Bu telefon numarası (${phoneNumber}) zaten sizin firmanızda kayıtlı!\n\nLütfen farklı bir telefon numarası kullanın.`);
          return;
        }
      }

      const newOperator = { 
        name, 
        surname, 
        phoneNumber: cleanedPhoneNumber,
        phoneNumberDisplay: phoneNumber,
        mesaiBaslangic,
        mesaiBitis,
        userEmail: userEmail,
        createdBy: auth.currentUser?.email,
        createdAt: toIsoUtcString(new Date())
      };

      // User email bazlı operatör ekleme
      const docRef = await addDoc(collection(db, `users/${userEmail}/operators`), newOperator);
      
      // Operatör telefon numarası mapping'i oluştur (Android/iOS için)
      await addDoc(collection(db, 'operators'), {
        phoneNumber: cleanedPhoneNumber,
        userEmail: userEmail,
        operatorId: docRef.id,
        name: `${name} ${surname}`,
        createdAt: toIsoUtcString(new Date())
      });

      // Local state'i güncelle
      const updatedOperators = [...operators, { id: docRef.id, ...newOperator }];
      setOperators(updatedOperators);

      // Setup progress kontrolü - En az bir operatör var mı?
      const userDocRef = doc(db, 'users', userEmail);
      await updateDoc(userDocRef, {
        'setupProgress.operators': updatedOperators.length > 0
      });

      console.log('✅ Operatör eklendi, setup progress güncellendi:', {
        operatorCount: updatedOperators.length,
        setupCompleted: updatedOperators.length > 0
      });
      
      setName('');
      setSurname('');
      setPhoneNumber('+90 ');
      setMesaiBaslangic('09:00');
      setMesaiBitis('18:00');
      
      alert('Operatör başarıyla eklendi!');
    } catch (error) {
      console.error('Operatör eklenirken hata:', error);
      alert('Operatör eklenirken hata: ' + error.message);
    }
  };

  const handleEdit = (operator) => {
    setSelectedOperator(operator);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async (updatedOperator) => {
    try {
      // Eğer telefon numarası değiştirilmişse, duplicate kontrolü yap
      if (updatedOperator.phoneNumber !== selectedOperator.phoneNumber) {
        const existingOperatorQuery = query(
          collection(db, 'operators'),
          where('phoneNumber', '==', updatedOperator.phoneNumber)
        );
        const existingOperatorSnapshot = await getDocs(existingOperatorQuery);
        
        if (!existingOperatorSnapshot.empty) {
          const existingOperator = existingOperatorSnapshot.docs[0].data();
          // Mevcut operatörün kendisi değilse ve farklı firma/operatörse
          if (existingOperator.operatorId !== selectedOperator.id) {
            if (existingOperator.userEmail !== userEmail) {
              alert(`Bu telefon numarası (${updatedOperator.phoneNumberDisplay}) zaten başka bir firma tarafından kullanılıyor!\n\nOperatör: ${existingOperator.name}\nFirma: ${existingOperator.userEmail}\n\nLütfen farklı bir telefon numarası kullanın.`);
              return;
            } else {
              alert(`Bu telefon numarası (${updatedOperator.phoneNumberDisplay}) zaten sizin firmanızda başka bir operatör tarafından kullanılıyor!\n\nLütfen farklı bir telefon numarası kullanın.`);
              return;
            }
          }
        }
      }

      // Doğru path ile güncelleme
      const docRef = doc(db, `users/${userEmail}/operators`, selectedOperator.id);
      const updateData = {
        ...updatedOperator,
        updatedAt: toIsoUtcString(new Date()),
        updatedBy: auth.currentUser?.email
      };
      
      await updateDoc(docRef, updateData);
      
      // Operatör mapping'ini de güncelle
      const operatorMappingQuery = query(
        collection(db, 'operators'), 
        where('operatorId', '==', selectedOperator.id)
      );
      const mappingSnapshot = await getDocs(operatorMappingQuery);
      
      if (!mappingSnapshot.empty) {
        const mappingDoc = mappingSnapshot.docs[0];
        await updateDoc(doc(db, 'operators', mappingDoc.id), {
          phoneNumber: updatedOperator.phoneNumber,
          name: `${updatedOperator.name} ${updatedOperator.surname}`,
          updatedAt: toIsoUtcString(new Date())
        });
      }
      
      // Local state'i güncelle
      setOperators(operators.map(op => 
        op.id === selectedOperator.id ? { ...op, ...updateData } : op
      ));
      
      alert('Operatör başarıyla güncellendi!');
    } catch (error) {
      console.error('Operatör güncellenirken hata:', error);
      alert('Operatör güncellenirken hata: ' + error.message);
    }
  };

  const handleDelete = async (operatorId) => {
    // Silinecek operatörü bul
    const operatorToDelete = operators.find(op => op.id === operatorId);
    if (!operatorToDelete) {
      alert('Operatör bulunamadı!');
      return;
    }

    const confirmMessage = `Bu operatörü silmek istediğinizden emin misiniz?\n\n${operatorToDelete.name} ${operatorToDelete.surname}\n${operatorToDelete.phoneNumberDisplay || operatorToDelete.phoneNumber}\n\nBu işlem operatörün Firebase hesabını da kalıcı olarak silecektir!`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      // Önce operatörün telefon numarasıyla kayıtlı kullanıcı hesabını bul
      const operatorPhone = operatorToDelete.phoneNumber;
      
      // Global operators koleksiyonundan operatörün userEmail'ini bul
      const operatorMappingQuery = query(
        collection(db, 'operators'), 
        where('operatorId', '==', operatorId)
      );
      const mappingSnapshot = await getDocs(operatorMappingQuery);
      
      let operatorUserEmail = null;
      if (!mappingSnapshot.empty) {
        const mappingDoc = mappingSnapshot.docs[0];
        operatorUserEmail = mappingDoc.data().userEmail;
        
        // Mapping'i sil
        await deleteDoc(doc(db, 'operators', mappingDoc.id));
      }

      // Operatör verilerini Firestore'dan sil
      await deleteDoc(doc(db, `users/${userEmail}/operators`, operatorId));
      
      // Local state'den kaldır
      const updatedOperators = operators.filter(operator => operator.id !== operatorId);
      setOperators(updatedOperators);

      // Setup progress kontrolü - Operatör kaldıktan sonra kontrol et
      const userDocRef = doc(db, 'users', userEmail);
      await updateDoc(userDocRef, {
        'setupProgress.operators': updatedOperators.length > 0
      });

      console.log('✅ Operatör silindi, setup progress güncellendi:', {
        operatorCount: updatedOperators.length,
        setupCompleted: updatedOperators.length > 0
      });
      
      // Operatörün telefon numarasıyla Firebase Authentication'dan da sil
      try {
        const functions = getFunctions();
        const deleteOperatorAuth = httpsCallable(functions, 'deleteOperatorAuth');
        
        const result = await deleteOperatorAuth({ 
          operatorPhoneNumber: operatorToDelete.phoneNumber,
          firmOwnerEmail: userEmail
        });
        
        console.log('Operatör hesabı silindi:', result.data);
        alert(`Operatör başarıyla silindi!\n\nOperatörün Firebase hesabı da kalıcı olarak silindi.`);
      } catch (error) {
        console.error('Operatör hesabı silinirken hata:', error);
        alert(`Operatör Firestore'dan silindi ancak Firebase hesabı silinirken hata oluştu:\n${error.message}\n\nFirestore verileri başarıyla temizlendi.`);
      }
      
    } catch (error) {
      console.error('Operatör silinirken hata:', error);
      alert('Operatör silinirken hata: ' + error.message);
    }
  };

  return (
    <div className="operator-ekle-container">
      <h2>Operatör Tanıtım</h2>
      <p className="user-info">Kullanıcı: {userEmail}</p>

      <form onSubmit={handleSubmit} className="operator-form">
        <TextField
          fullWidth
          margin="normal"
          label="Ad"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Soyad"
          type="text"
          value={surname}
          onChange={(e) => setSurname(e.target.value)}
          required
        />
        <TextField
          fullWidth
          margin="normal"
          label="Telefon"
          type="tel"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(formatPhoneDisplay(e.target.value))}
          required
          inputProps={{
            pattern: "\\+90 [0-9]{3} [0-9]{3} [0-9]{2} [0-9]{2}",
          }}
          helperText="Format: +90 5XX XXX XX XX"
        />
        <div className="mesai-container">
          <TextField
            label="Mesai Başı"
            type="time"
            value={mesaiBaslangic}
            onChange={(e) => setMesaiBaslangic(e.target.value)}
            onClick={(e) => {
              // Input'a focus ver ve time picker'ı aç
              const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
              if (input?.showPicker) {
                input.showPicker();
              } else if (input) {
                input.focus();
              }
            }}
            required
            InputLabelProps={{ shrink: true }}
            sx={{ 
              flex: 1, 
              marginRight: 1,
              '& .MuiInputBase-input': {
                cursor: 'pointer'
              },
              '& .MuiInputBase-root': {
                cursor: 'pointer'
              }
            }}
          />
          <TextField
            label="Mesai Sonu"
            type="time"
            value={mesaiBitis}
            onChange={(e) => setMesaiBitis(e.target.value)}
            onClick={(e) => {
              // Input'a focus ver ve time picker'ı aç
              const input = e.target.closest('.MuiInputBase-root')?.querySelector('input');
              if (input?.showPicker) {
                input.showPicker();
              } else if (input) {
                input.focus();
              }
            }}
            required
            InputLabelProps={{ shrink: true }}
            sx={{ 
              flex: 1, 
              marginLeft: 1,
              '& .MuiInputBase-input': {
                cursor: 'pointer'
              },
              '& .MuiInputBase-root': {
                cursor: 'pointer'
              }
            }}
          />
        </div>
        <Button 
          type="submit" 
          variant="contained" 
          color="primary" 
          disabled={!userEmail}
          sx={{ marginTop: 2 }}
        >
          Operatör Ekle
        </Button>
      </form>

      <h3>Kullanıcı Operatörleri ({operators.length})</h3>
      {operators.length === 0 ? (
        <p className="no-operators">Henüz operatör eklenmemiş.</p>
      ) : (
        <table className="operator-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} style={{cursor: 'pointer'}}>Ad {renderSortIcon('name')}</th>
              <th onClick={() => handleSort('surname')} style={{cursor: 'pointer'}}>Soyad {renderSortIcon('surname')}</th>
              <th onClick={() => handleSort('phoneNumber')} style={{cursor: 'pointer'}}>Telefon {renderSortIcon('phoneNumber')}</th>
              <th>Mesai Saatleri</th>
              <th>Eklenme Tarihi</th>
              <th>İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {sortedOperators.map((operator) => (
              <tr key={operator.id}>
                <td>{operator.name}</td>
                <td>{operator.surname}</td>
                <td>{operator.phoneNumberDisplay || operator.phoneNumber}</td>
                <td style={{fontSize: '0.9em', textAlign: 'center'}}>
                  {(operator.mesaiBaslangic || '09:00')} - {(operator.mesaiBitis || '18:00')}
                </td>
                <td style={{fontSize: '0.8em', color: '#666'}}>
                  {operator.createdAt ? formatTrDateOnly(operator.createdAt) : 'Bilinmiyor'}
                </td>
                <td>
                  <IconButton 
                    onClick={() => handleEdit(operator)} 
                    className="edit-btn"
                    size="small"
                    title="Düzenle"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => handleDelete(operator.id)} 
                    className="delete-btn"
                    size="small"
                    title="Sil"
                  >
                    <DeleteIcon />
                  </IconButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <EditOperatorModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        operator={selectedOperator}
        onSave={handleSaveEdit}
      />
    </div>
  );
};

export default OperatorEkle;